import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Printer, Save, ShieldAlert, Lock, MessageSquareQuote } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Block, FlowTable, AnswerField, Bullets } from "@/components/course/LessonParts";
import { SectionThread, useDayComments } from "@/components/course/SectionThread";
import { AnswerAdvice } from "@/components/course/AnswerAdvice";
import { AiInsights, useInsights, SharedInsight, useMyInsights } from "@/components/course/AiInsights";
import { StartCallButton } from "@/components/site/CallWindow";
import { StudentQuestionnaireReveal, HostQuestionnaire } from "@/components/course/QuestionnaireResult";
import { StatusBadge } from "@/components/site/StatusBadge";
import { Spinner } from "@/components/site/ProtectedRoute";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { fetchCourse } from "@/lib/course";
import { useSeo } from "@/lib/seo";
import { DAY_STATUS, PRIVACY_NOTE } from "@/lib/constants";

function PrintBar({ day, isHost, studentId }) {
  const cls = "inline-flex items-center gap-2 rounded-xl border border-[#0B2A4A]/20 bg-white px-3.5 py-2 text-xs sm:text-sm font-semibold hover:border-[#1A9AA8]";
  return (
    <div className="flex flex-wrap gap-2" data-testid="lesson-print-bar">
      <Link to={`/print/day/${day}?mode=filled${studentId ? `&student=${studentId}` : ""}`} className={cls} data-testid="print-worksheet-btn"><Printer className="h-4 w-4" /> Print this day's worksheet</Link>
      <Link to={`/print/day/${day}?mode=blank`} className={cls} data-testid="print-day-blank-btn"><Printer className="h-4 w-4" /> Blank copy</Link>
      <Link to="/print/worksheets?mode=filled" className={cls} data-testid="print-all-btn"><Printer className="h-4 w-4" /> Print all participant worksheets</Link>
      {isHost && <Link to={`/print/host-plan/${day}`} className={cls} data-testid="print-host-plan-day-btn"><Printer className="h-4 w-4" /> Print the host lesson plan</Link>}
    </div>
  );
}

export default function LessonPage() {
  const { day: dayParam, userId } = useParams();
  const dayNum = Number(dayParam);
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);
  const [rec, setRec] = useState({ answers: {}, deliverables: {}, status: "not_started" });
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const isHost = user.role === "host";
  const teaching = isHost && !!userId;
  const readOnly = isHost && !teaching;
  const [student, setStudent] = useState(null);
  const base = teaching ? `/host/teach/${userId}/day` : "/course/day";
  const { comments, post, markRead } = useDayComments(dayNum, teaching ? userId : undefined);
  const thread = (section, label) => (!readOnly && !error ? <SectionThread section={section} comments={comments} post={post} markRead={markRead} me={teaching ? "host" : "student"} label={label} /> : null);
  const advice = (id) => (!readOnly && !error ? <AnswerAdvice fieldId={id} comments={comments} post={post} markRead={markRead} me={teaching ? "host" : "student"} /> : null);
  const insights = useInsights(teaching ? userId : null);
  const myInsights = useMyInsights();
  useSeo({ title: `Day ${dayNum} lesson`, path: `/course/day/${dayNum}`, noindex: true });

  const loadProgress = () => {
    const req = teaching ? api.get(`/admin/students/${userId}/print`).then(({ data }) => { setStudent(data.user); return data.progress; }) : api.get("/course/progress").then((r) => r.data);
    req.then((data) => {
      const p = data.find((x) => x.day === dayNum);
      setRec(p ? { answers: p.answers || {}, deliverables: p.deliverables || {}, status: p.status } : { answers: {}, deliverables: {}, status: "not_started" });
      setSaved(p || null);
      setDirty(false);
    }).catch(() => {});
  };

  useEffect(() => {
    setCourse(null);
    fetchCourse().then(setCourse).catch((e) => setError(e.response?.status === 403 ? "locked" : formatApiError(e)));
    if (!readOnly) loadProgress();
  }, [dayNum, readOnly, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async (status = rec.status, silent = false) => {
    setBusy(true);
    try {
      const { data } = await api.put(teaching ? `/admin/students/${userId}/progress/${dayNum}` : `/course/progress/${dayNum}`, { ...rec, status });
      setRec((r) => ({ ...r, status: data.status }));
      setSaved(data);
      setDirty(false);
      if (!silent) toast.success(teaching ? "Saved to the student's profile" : "Saved to your profile");
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Auto-save answers shortly after typing stops
  useEffect(() => {
    if (!dirty || readOnly) return undefined;
    const t = setTimeout(() => save(rec.status, true), 1200);
    return () => clearTimeout(t);
  }, [rec, dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live sync: pick up answers typed on the other side (host teaching <-> student)
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty || busy;
  useEffect(() => {
    if (readOnly) return undefined;
    const t = setInterval(() => { if (!dirtyRef.current && !document.hidden) loadProgress(); }, 4000);
    return () => clearInterval(t);
  }, [dayNum, readOnly, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (dayNum < 1 || dayNum > 7 || Number.isNaN(dayNum)) return <Navigate to="/dashboard" replace />;
  if (error === "locked")
    return (
      <section className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="lesson-locked">
        <Lock className="h-10 w-10 mx-auto text-[#F08A3C]" />
        <h1 className="mt-4 text-3xl font-bold text-[#0B2A4A]">Lessons are locked</h1>
        <p className="mt-3 text-slate-600">Your application must be approved before lessons unlock.</p>
        <Link to="/dashboard" className="mt-6 inline-block font-semibold text-[#F08A3C]">View my application status</Link>
      </section>
    );
  if (error) return <p className="text-center py-24 text-rose-600">{error}</p>;
  if (!course) return <Spinner />;

  const day = course.days[dayNum - 1];
  const ws = course.worksheets.find((w) => w.id === day.worksheet_id);
  const setAnswer = (id, v) => { setRec((r) => ({ ...r, answers: { ...r.answers, [id]: v } })); setDirty(true); };
  const toggleDel = (id) => { setRec((r) => ({ ...r, deliverables: { ...r.deliverables, [id]: !r.deliverables[id] } })); setDirty(true); };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 flex flex-col gap-6" data-testid={`lesson-day-${dayNum}`}>
      <Link to={teaching && student ? `/host/people/${encodeURIComponent(student.email)}` : isHost ? "/host" : "/dashboard"} className="text-sm font-semibold text-[#147C88] inline-flex items-center gap-1" data-testid="lesson-back-link"><ArrowLeft className="h-4 w-4" /> Back</Link>
      {teaching && (
        <div className="rounded-2xl bg-[#0B2A4A] text-[#F5ECD7] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="teaching-banner">
          <p className="text-sm"><span className="font-mono-ui text-[11px] tracking-widest text-[#F08A3C] mr-2">TEACHING MODE</span>Live lesson with <strong data-testid="teaching-student-name">{student?.name || "student"}</strong>. Host notes are visible only to you.</p>
          <div className="flex flex-wrap gap-2">
            <StartCallButton studentId={userId} className="!py-1.5 !px-3 !text-xs" />
            <button onClick={loadProgress} className="rounded-lg bg-white/10 border border-white/25 px-3 py-1.5 text-xs font-semibold" data-testid="teaching-refresh-btn">Refresh student's answers</button>
          </div>
        </div>
      )}
      <header className="flex flex-col gap-4">
        <p className="font-mono-ui text-sm font-bold text-[#F08A3C]">DAY {dayNum} OF 7 · 60-MINUTE LESSON</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B2A4A]">{day.title}</h1>
        {!readOnly && <div className="flex items-center gap-3"><StatusBadge status={rec.status} testId="lesson-status-badge" />{saved?.updated_at && <span className="text-xs text-slate-500 font-mono-ui" data-testid="lesson-last-saved">Saved {fmtDate(saved.updated_at)}</span>}</div>}
        <PrintBar day={dayNum} isHost={isHost} studentId={teaching ? userId : null} />
      </header>

      <Block label="Lesson purpose & outcome" testId="lesson-purpose">
        <p className="text-slate-700 leading-relaxed"><strong className="text-[#0B2A4A]">Purpose:</strong> {day.purpose}</p>
        <p className="mt-3 text-slate-700 leading-relaxed"><strong className="text-[#0B2A4A]">Outcome:</strong> {day.outcome}</p>
        <div className="mt-6 rounded-xl bg-[#0B2A4A] text-[#F5ECD7] p-5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#F08A3C]">Opening question</p>
          <p className="mt-2 font-display text-lg">{day.opening_question}</p>
        </div>
        {isHost && <p className="mt-5 text-sm text-slate-600 leading-relaxed rounded-xl border border-dashed border-[#F08A3C] p-3" data-testid="host-example"><strong className="text-[#D96F28]">Host only · Your example:</strong> {day.host_example}</p>}
      </Block>

      {day.schedule?.length > 0 && (
        <Block label="Today's timetable" testId="lesson-schedule">
          <dl className="flex flex-col divide-y divide-[#0B2A4A]/10 text-sm">
            {day.schedule.map((s) => (
              <div key={s.time + s.block} className="py-2.5 grid sm:grid-cols-[150px_110px_1fr] gap-1"><dt className="font-mono-ui text-[#147C88]">{s.time}</dt><dd className="font-semibold text-[#0B2A4A]">{s.block}</dd><dd className="text-slate-700">{s.what}</dd></div>
            ))}
          </dl>
        </Block>
      )}

      {isHost && day.host_guide?.prepares && (
        <Block label="Host only · Greg prepares & notes" testId="lesson-host-prepares">
          <Bullets items={[...day.host_guide.prepares, ...(day.host_guide.teaching_notes || [])]} />
        </Block>
      )}
      {isHost && <Block label="Host only · Teach these points" testId="lesson-teach-points"><Bullets items={day.teach_points} /></Block>}
      {isHost && <Block label="Host only · Live 2-hour teaching flow" title="How class runs today" testId="lesson-flow-block"><FlowTable flow={day.flow} showHost={isHost} /></Block>}

      <Block label="In-person activity" testId="lesson-activity">
        <p className="text-slate-700 leading-relaxed">{day.in_person_activity}</p>
        {isHost && day.host_guide?.practice_lab?.length > 0 && <div className="mt-4 text-sm text-slate-600 space-y-2" data-testid="host-practice-lab"><p className="text-[11px] font-bold uppercase tracking-wider text-[#D96F28]">Host only · Practice lab & close</p>{[...day.host_guide.practice_lab, ...day.host_guide.close].map((t) => <p key={t}>{t}</p>)}</div>}
      </Block>

      {dayNum === 6 && !isHost && <StudentQuestionnaireReveal />}
      {dayNum === 6 && teaching && <HostQuestionnaire userId={userId} />}

      {!isHost && (
        <div className="flex gap-3 rounded-2xl bg-[#F5ECD7] p-4 text-sm text-slate-700" data-testid="lesson-privacy-note"><ShieldAlert className="h-5 w-5 text-[#F08A3C] shrink-0" /> {PRIVACY_NOTE}</div>
      )}

      <Block label="In class" title="Questions to answer in class" testId="lesson-class-questions">
        <div className="flex flex-col gap-5">
          {day.class_questions.map((q) => <AnswerField key={q.id} field={q} value={rec.answers[q.id]} onChange={setAnswer} disabled={readOnly}>{advice(q.id)}</AnswerField>)}
          <div className="rounded-xl border border-dashed border-[#1A9AA8] p-4">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#147C88] mb-2"><MessageSquareQuote className="h-4 w-4" /> Ask Mr. Greg</p>
            <AnswerField field={{ id: "host_question", prompt: "Question for the host" }} value={rec.answers.host_question} onChange={setAnswer} disabled={readOnly} rows={2}>{advice("host_question")}</AnswerField>
          </div>
        </div>
        {thread("class")}
      </Block>

      <Block label="At home" title="Homework" testId="lesson-homework">
        <p className="text-slate-700 leading-relaxed mb-6">{day.homework}</p>
        <div className="flex flex-col gap-5">
          {day.homework_questions.map((q) => <AnswerField key={q.id} field={q} value={rec.answers[q.id]} onChange={setAnswer} disabled={readOnly}>{advice(q.id)}</AnswerField>)}
          <AnswerField field={{ id: "homework_evidence", prompt: "Evidence: sources, dates, links, or notes (optional)" }} value={rec.answers.homework_evidence} onChange={setAnswer} disabled={readOnly} rows={2}>{advice("homework_evidence")}</AnswerField>
        </div>
        {thread("homework")}
      </Block>

      {ws && (
        <Block label={`Participant worksheet ${ws.number}`} title={ws.title} testId="lesson-worksheet">
          <p className="text-sm text-slate-600 mb-6">{ws.intro}</p>
          <div className="flex flex-col gap-5">
            {ws.fields.map((q) => <AnswerField key={q.id} field={q} value={rec.answers[q.id]} onChange={setAnswer} disabled={readOnly}>{advice(q.id)}</AnswerField>)}
          </div>
          {thread("worksheet")}
        </Block>
      )}

      <Block label="Deliverables" title="Save to your student profile" testId="lesson-deliverables">
        <ul className="flex flex-col gap-3">
          {day.deliverables.map((d) => (
            <li key={d.id}>
              <label className="flex items-center gap-3 cursor-pointer text-slate-700">
                <input type="checkbox" className="h-5 w-5 accent-[#1A9AA8]" checked={!!rec.deliverables[d.id]} disabled={readOnly} onChange={() => toggleDel(d.id)} data-testid={`deliverable-${d.id}`} />
                {d.label}
              </label>
            </li>
          ))}
        </ul>
        {thread("deliverables")}
      </Block>

      {!readOnly && (
        <Block label="Coaching" title={`General advice for Day ${dayNum}`} testId="lesson-general-thread">
          <p className="text-sm text-slate-600">{teaching ? "General advice for this student about today's lesson." : "Anything else about today's lesson? Mr. Greg will reply right here."}</p>
          {thread("general", "Conversation about this day")}
        </Block>
      )}
      {teaching && <AiInsights studentId={userId} scope={`day:${dayNum}`} list={insights.list} reload={insights.load} title={`AI insights for Day ${dayNum}`} />}
      {!isHost && <SharedInsight insight={myInsights.find((i) => i.scope === `day:${dayNum}`)} title={`Insights from Mr. Greg for Day ${dayNum}`} />}

      {saved?.feedback && (
        <Block label="Feedback from Mr. Greg" testId="lesson-host-feedback"><p className="whitespace-pre-wrap text-slate-700">{saved.feedback}</p></Block>
      )}

      {!readOnly && (
        <div className="sticky bottom-3 z-20 bg-white/95 backdrop-blur border border-[#0B2A4A]/10 shadow-xl rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="lesson-save-bar">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-[#0B2A4A]">Status</span>
            <Select value={rec.status} onValueChange={(v) => { setRec((r) => ({ ...r, status: v })); setDirty(true); }}>
              <SelectTrigger className="w-52" data-testid="lesson-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAY_STATUS.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`status-option-${s.value}`}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {dirty ? <span className="text-xs text-amber-700" data-testid="lesson-unsaved">Saving...</span> : saved ? <span className="text-xs text-emerald-700" data-testid="lesson-autosaved">All answers saved{teaching ? " to the student's worksheet" : ""}</span> : null}
          </div>
          <div className="flex gap-2">
            <button onClick={() => save()} disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-[#0B2A4A] text-[#F5ECD7] px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="day-worksheet-save-btn"><Save className="h-4 w-4" /> Save</button>
            <button onClick={() => save("homework_submitted")} disabled={busy} className="rounded-xl bg-[#F08A3C] text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="worksheet-submit-btn">Submit homework</button>
          </div>
        </div>
      )}

      <nav className="flex justify-between pt-4">
        {dayNum > 1 ? <Link to={`${base}/${dayNum - 1}`} className="inline-flex items-center gap-1 font-semibold text-[#147C88]" data-testid="lesson-prev-day"><ArrowLeft className="h-4 w-4" /> Day {dayNum - 1}</Link> : <span />}
        {dayNum < 7 && <Link to={`${base}/${dayNum + 1}`} className="inline-flex items-center gap-1 font-semibold text-[#147C88]" data-testid="lesson-next-day">Day {dayNum + 1} <ArrowRight className="h-4 w-4" /></Link>}
      </nav>
    </div>
  );
}
